// ─────────────────────────────────────────────────────────────
// Shown after signup (or from settings) to create transaction PIN.
// Two-step: enter → confirm.
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";
import PinInput from "../../components/feature/PinInput";

export default function CreatePinPage() {
  const navigate     = useNavigate();
  const { state }    = useLocation();
  const { updateUser } = useAuth();
  const redirectTo   = state?.redirectTo || "/home";

  const [step,    setStep]    = useState(1);   // 1 = enter, 2 = confirm
  const [pin,     setPin]     = useState("");
  const [confirm, setConfirm] = useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);

  const handlePinChange = (val) => {
    setError("");
    setPin(val);
    if (val.length === 4) {
      // Auto-advance to confirm step
      setTimeout(() => setStep(2), 200);
    }
  };

  const handleConfirmChange = (val) => {
    setError("");
    setConfirm(val);
    if (val.length === 4) {
      handleSubmit(val);
    }
  };

  const handleSubmit = async (confirmVal) => {
    const c = confirmVal || confirm;
    if (c !== pin) {
      setError("PINs do not match. Try again.");
      setConfirm("");
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post("/mtrade/user/transaction-pin/create", {
        pin,
        confirmPin: c,
      });
      if (res.data.status) {
        updateUser({ hasTransactionPin: true });
        toast.success("Transaction PIN created!");
        navigate(redirectTo, { replace: true });
      } else {
        setError(res.data.message || "Failed to create PIN");
        setConfirm("");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create PIN");
      setConfirm("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-5 border-b border-border">
        <div className="flex items-center gap-3">
          {step === 2 ? (
            <button
              onClick={() => { setStep(1); setConfirm(""); setError(""); }}
              className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center"
            >
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
          ) : (
            <div className="w-9 h-9" />
          )}
          <div>
            <h1 className="text-primary font-bold text-lg font-display">
              {step === 1 ? "Create Transaction PIN" : "Confirm PIN"}
            </h1>
            <p className="text-muted text-xs">
              {step === 1 ? "Choose a 4-digit PIN for all payments" : "Re-enter your PIN to confirm"}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-8 py-10">
        {/* Progress dots */}
        <div className="flex gap-2 mb-10">
          {[1,2].map(s => (
            <div key={s} className={`h-1.5 rounded-full transition-all ${
              s <= step ? "w-8 bg-brand" : "w-4 bg-border-strong"
            }`} />
          ))}
        </div>

        {step === 1 ? (
          <PinInput
            value={pin}
            onChange={handlePinChange}
            label="Create a 4-digit PIN"
            error={error}
            disabled={loading}
          />
        ) : (
          <PinInput
            value={confirm}
            onChange={handleConfirmChange}
            label="Confirm your PIN"
            error={error}
            disabled={loading}
          />
        )}

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-brand">
            <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand-light)", borderTopColor: "var(--color-brand)" }} />
            <span className="text-sm font-medium">Creating PIN…</span>
          </div>
        )}

        <p className="text-muted text-xs text-center mt-10 max-w-[220px] leading-relaxed">
          Keep this PIN private. You'll need it for every transaction.
        </p>
      </div>
    </div>
  );
}
